import { create } from "zustand";
import { getAllUsers, getUsersBySection, getUsersByDepartment } from "api/services/usersServices";
import { getAssignableVerificationUsers } from "api/services/obligationsServices";
import { mapUsersResponse } from "../utils/ODGeneralUtils";

// ─── Config initial state ────────────────────────────────────────────────────
// All real values are defined by useVariantConfig, everything is temporarily empty to define the store's shape
const tempConfigInitialState = {
  activeVariant: null,
  compact: {},
  readOnly: false,
  isOnlyVerificatorUser: false,
  features: {},
  headerSections: {},
  sidebarSections: {},
  bodySections: {},
  display: {
    showSidebar: true,
    showHeader: true,
    showBody: true,
  },
  callbacks: {
    onObligationDataChange: null,
    onRecurrenceDataChange: null,
  },
};

const interactionInitialState = {
  interactionResetKey: 0,
  selectedObligation: null,
  selectedRecurrence: null,
  selectedFile: null,
  isRecurrenceComplete: false,
  areVerificationCriteriaComplete: false,
  isUserVerificationAssignee: false,
  isSelectedRecurrenceVerifiable: false,
  isVerificationRequiredForGroup: false,
  hasStrictCompletion: false,
  areVerificationCriteriaAvailable: false,
  isCurrentUserAssignedAsResponsible: false,
  isUserVerificatorForSelectedRecurrence: false,
  isLoadingObligation: true,
  isLoadingRecurrence: false,
  isSelectingRecurrence: false,
  expandedSections: { sidebar: [], body: [] },
  hasAnyCategories: false,
};

const usersInitialState = {
  users: [],
  loadingUsers: false,
  usersError: null,
  verificationUsers: [],
  loadingVerificationUsers: false,
  verificationUsersError: null,
  departmentScopedVerificationUsers: [],
  loadingDepartmentScopedVerificationUsers: false,
  departmentScopedVerificationUsersError: null,
};

const criteriaHistoryInitialState = {
  criteriaHistoryMap: {}, // { [criterion_id]: { items: [], total: 0 } }
  loadingCriteriaHistory: false,
  criteriaHistoryError: null,
};

// ─── Store ───────────────────────────────────────────────────────────────────
const useObligationDetailsStore = create((set, get) => ({
  // Config slice
  ...tempConfigInitialState,

  setConfig: (resolvedConfig) => set((s) => {
    // Only recalculate expandedSections from defaultExpanded flags when the variant is new or changing
    // This allows preserving the user's interaction state when the same variant is being recalculated after interaction like refetches
    const isVariantChange = s.activeVariant !== resolvedConfig.activeVariant;
    if (!isVariantChange) return { ...resolvedConfig, expandedSections: s.expandedSections };

    const sidebarExpanded = Object.entries(resolvedConfig.sidebarSections || {})
      .filter(([, cfg]) => cfg.defaultExpanded)
      .map(([key]) => key);
    const bodyExpanded = Object.entries(resolvedConfig.bodySections || {})
      .filter(([, cfg]) => cfg.defaultExpanded)
      .map(([key]) => key);
    return {
      ...resolvedConfig,
      expandedSections: { sidebar: sidebarExpanded, body: bodyExpanded },
    };
  }),
  resetConfig: () => set({ ...tempConfigInitialState }),

  // Toggles a subsection's expanded state within a given main section scope
  // Currently only used by ('sidebar' | 'body')
  toggleSection: (scope, key) => set((s) => {
    const current = s.expandedSections?.[scope] ?? [];
    return {
      expandedSections: {
        ...s.expandedSections,
        [scope]: current.includes(key)
          ? current.filter((k) => k !== key)
          : [...current, key],
      },
    };
  }),

  // Interaction slice
  ...interactionInitialState,

  setSelectedObligation: (obligation) => set({ selectedObligation: obligation || null }),
  setLoadingObligation: (val) => set({ isLoadingObligation: val }),
  setSelectedRecurrence: (recurrence) => {
    const { selectedRecurrence, selectedFile } = get();
    const isRecurrenceDifferent = (selectedRecurrence?.obligation_id ?? null) !== (recurrence?.obligation_id ?? null);
    // Reference type files (saved as document) belong to the obligation group
    // The open file would still exist even if the recurrence changes
    const shouldClearFile =
      isRecurrenceDifferent &&
      (selectedFile?.type !== "document");

    set({
      selectedRecurrence: recurrence,
      isRecurrenceComplete: recurrence?.status?.toLowerCase().includes("complete") ?? false,
      areVerificationCriteriaComplete: recurrence?.aggregate_verification_status?.toLowerCase().includes("accepted") ?? false,
      // Clear criteria history on recurrence change so widgets can't render stale data
      // from the previous recurrence
      ...(isRecurrenceDifferent ? {
        criteriaHistoryMap: {},
        loadingCriteriaHistory: false,
        criteriaHistoryError: null
      } : {}),
      ...(shouldClearFile ? { selectedFile: null } : {}),
    });
  },
  setLoadingRecurrence: (val) => set({ isLoadingRecurrence: val }),
  setIsSelectingRecurrence: (val) => set({ isSelectingRecurrence: !!val }),
  setSelectedFile: (file) => set({ selectedFile: file || null }),
  setHasAnyCategories: (val) => set({ hasAnyCategories: val }),
  setUserVerificationAssignee: (val) => set({ isUserVerificationAssignee: val }),
  setIsSelectedRecurrenceVerifiable: (val) => set({ isSelectedRecurrenceVerifiable: val }),
  setIsVerificationRequiredForGroup: (val) => set({ isVerificationRequiredForGroup: val }),
  setHasStrictCompletion: (val) => set({ hasStrictCompletion: val }),
  setAreVerificationCriteriaAvailable: (val) => set({ areVerificationCriteriaAvailable: val }),
  setIsCurrentUserAssignedAsResponsible: (val) => set({ isCurrentUserAssignedAsResponsible: val }),
  setIsUserVerificatorForSelectedRecurrence: (val) => set({ isUserVerificatorForSelectedRecurrence: val }),
  openFile: (file) => set({ selectedFile: file || null }),
  closeFile: () => set({ selectedFile: null }),

  // ── Lifecycle actions ────────────────────────────────────────────────────

  // To be used on first mount or obligationId change — clears all state
  initialize: () => set((s) => ({
    ...tempConfigInitialState,
    ...interactionInitialState,
    ...usersInitialState,
    ...criteriaHistoryInitialState,
    // Incrementing the reset key allows a full recalculation of values when the module is reopened
    // even if the obligation data is identical to the previous session
    // In the uncontrolled path this can happen if the module is opened then closed then opened again
    interactionResetKey: (s.interactionResetKey ?? 0) + 1,
  })),

  // Variant flip from an existing open session — clears all interaction and user state
  // After the new variant settles everything will be recalculated and established from the new effective variant's config
  transitionIntoVariant: () => set((s) => ({
    ...interactionInitialState,
    ...usersInitialState,
    ...criteriaHistoryInitialState,
    // Preserve existing selected recurrence so the timeline can reselect the same one after settling
    selectedRecurrence: s.selectedRecurrence?.obligation_id
      ? { obligation_id: s.selectedRecurrence.obligation_id }
      : null,
    interactionResetKey: (s.interactionResetKey ?? 0) + 1,
  })),

  // To be called for a complete wipe of the store
  teardown: () => set({
    ...tempConfigInitialState,
    ...interactionInitialState,
    ...usersInitialState,
    ...criteriaHistoryInitialState,
    activeVariant: null,
  }),

  activeVariant: null,

  // Users slice
  ...usersInitialState,

  fetchUsers: async (options = {}) => {
    const entityId = options?.entityId ?? null;
    const canUseFallback = get().features?.canUseFallbackUsers ?? true;

    if (!entityId && !canUseFallback) {
      set({ users: [], loadingUsers: false });
      return [];
    }

    set({ loadingUsers: true, usersError: null });

    try {
      const response = await (entityId ? getUsersBySection(entityId) : getAllUsers());
      const mappedUsers = mapUsersResponse(response);

      set({ users: mappedUsers, loadingUsers: false });
      return mappedUsers;
    } catch (error) {
      set({ users: [], usersError: error.message, loadingUsers: false });
      throw error;
    }
  },

  resetUsers: () => set({ users: [], usersError: null, loadingUsers: false }),

  fetchVerificationUsers: async () => {
    set({ loadingVerificationUsers: true, verificationUsersError: null });

    try {
      const response = await getAssignableVerificationUsers();
      const mappedUsers = mapUsersResponse(response);

      set({ verificationUsers: mappedUsers, loadingVerificationUsers: false });
      return mappedUsers;
    } catch (error) {
      set({ verificationUsers: [], verificationUsersError: error.message, loadingVerificationUsers: false });
      throw error;
    }
  },

  resetVerificationUsers: () => set({ verificationUsers: [], verificationUsersError: null, loadingVerificationUsers: false }),

  // Verification users slice (scoped to the obligation's main department)
  fetchDepartmentScopedVerificationUsers: async (departmentId) => {
    const canUseFallback = get().features?.canUseFallbackDepartmentScopedVerificationUsers ?? false;

    if (!departmentId && !canUseFallback) return;

    set({ loadingDepartmentScopedVerificationUsers: true, departmentScopedVerificationUsersError: null });

    try {
      const response = await (departmentId ? getUsersByDepartment(departmentId) : getAllUsers());
      const mappedUsers = mapUsersResponse(response);

      set({ departmentScopedVerificationUsers: mappedUsers, loadingDepartmentScopedVerificationUsers: false });
      return mappedUsers;
    } catch (error) {
      set({ departmentScopedVerificationUsers: [], departmentScopedVerificationUsersError: error.message, loadingDepartmentScopedVerificationUsers: false });
      throw error;
    }
  },

  resetDepartmentScopedVerificationUsers: () => set({ departmentScopedVerificationUsers: [], departmentScopedVerificationUsersError: null, loadingDepartmentScopedVerificationUsers: false }),

  // Criteria history slice
  ...criteriaHistoryInitialState,

  setCriteriaHistoryMap: (map) => set({ criteriaHistoryMap: map }),
  setLoadingCriteriaHistory: (val) => set({ loadingCriteriaHistory: val }),
  setCriteriaHistoryError: (err) => set({ criteriaHistoryError: err }),

  resetCriteriaHistory: () => set({ ...criteriaHistoryInitialState }),
}));

export default useObligationDetailsStore;